import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isFlashAdmin } from '../../utils/adminAccess';

interface WorkspaceLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  onExportReport?: () => void;
  onNewExercise?: () => void;
  primaryAction?: React.ReactNode;
}

export const WorkspaceLayout: React.FC<WorkspaceLayoutProps> = ({
  children,
  title,
  subtitle,
  onExportReport,
  onNewExercise,
  primaryAction,
}) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Persistent collapsed state for workspace sidebar
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('flashai.workspace_sidebar.collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const [tooltip, setTooltip] = useState<{ text: string; top: number } | null>(null);

  const toggleCollapsed = () => {
    setTooltip(null);
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('flashai.workspace_sidebar.collapsed', String(next));
      } catch {}
      return next;
    });
  };

  const orgName = '[ORGANISATION NAME]';

  const userInitials =
    user?.full_name
      ?.split(' ')
      .filter(Boolean)
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'VC';

  const displayName = user?.full_name || '[USER NAME]';
  const isAdmin = isFlashAdmin(user);

  const navItems = isAdmin
    ? [
        {
          label: 'Dashboard',
          path: '/dashboard',
          icon: (
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          ),
        },
        {
          label: 'Continuity plan',
          path: '/bcp',
          icon: (
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
        },
        {
          label: 'Impact analysis',
          path: '/impact-analysis',
          icon: (
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          ),
        },
        {
          label: 'Exercises',
          path: '/tabletop-exercises',
          icon: (
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        },
        {
          label: 'Actions',
          path: '/actions',
          badge: 14,
          icon: (
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          ),
        },
        {
          label: 'Documents',
          path: '/documents',
          icon: (
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
          ),
        },
        {
          label: 'Agents',
          path: '/agents',
          icon: (
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          ),
        },
      ]
    : [
        {
          label: 'Exercises',
          path: '/tabletop-exercises',
          icon: (
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        },
        {
          label: 'Continuity plan',
          path: '/bcp',
          icon: (
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
        },
      ];

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    navigate(`/bcp?search=${encodeURIComponent(searchQuery.trim())}`);
  };

  return (
    <div className="flex h-screen bg-[#F8FAFC] text-slate-800 antialiased overflow-hidden">
      {/* Left Sidebar */}
      <aside
        className={`bg-white border-r border-slate-200 flex flex-col flex-shrink-0 select-none z-20 transition-all duration-300 ease-in-out ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        {/* Brand / Logo & Toggle Button */}
        <div className="relative p-4 pb-3 border-b border-slate-100 flex items-center min-h-[61px]">
          <Link
            to={isAdmin ? "/admin" : "/tabletop-exercises"}
            className={`flex items-center overflow-hidden ${
              isCollapsed ? 'mx-auto justify-center' : ''
            }`}
          >
            <div
              className={`overflow-hidden flex-shrink-0 flex items-center transition-[width] duration-300 ease-in-out ${
                isCollapsed ? 'w-[34px] justify-start' : 'w-[145px]'
              }`}
            >
              <img
                src="/flashailogo.png"
                alt="FLASH"
                className="h-9 w-auto max-w-none object-contain object-left flex-shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          </Link>

          {/* Collapse/Expand Toggle Button */}
          <button
            type="button"
            onClick={toggleCollapsed}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors z-30 cursor-pointer"
          >
            {isCollapsed ? (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            )}
          </button>
        </div>

        {/* Workspace Dropdown */}
        {!isCollapsed ? (
          <div className="px-4 py-3 border-b border-slate-50">
            <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase mb-1">
              Workspace
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsOrgDropdownOpen((prev) => !prev)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold text-slate-800 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
              >
                <span className="truncate">{orgName}</span>
                <svg className="w-3.5 h-3.5 text-slate-400 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isOrgDropdownOpen && (
                <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-30 text-xs">
                  <div className="px-3 py-1.5 text-slate-500 font-medium border-b border-slate-100">
                    Switch Workspace
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsOrgDropdownOpen(false)}
                    className="w-full text-left px-3 py-2 text-slate-800 hover:bg-slate-50 font-semibold"
                  >
                    {orgName} (Current)
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="py-2 flex justify-center">
            <div
              onMouseEnter={(e) => {
                if (!isCollapsed) return;
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltip({
                  text: `Workspace: ${orgName}`,
                  top: rect.top + rect.height / 2,
                });
              }}
              onMouseLeave={() => setTooltip(null)}
              className="w-7 h-7 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] font-extrabold text-slate-600 cursor-default"
            >
              WS
            </div>
          </div>
        )}

        {/* Navigation items */}
        <nav
          onScroll={() => setTooltip(null)}
          className="flex-1 px-3 py-3 space-y-1.5 overflow-y-auto overflow-x-hidden [scrollbar-width:thin]"
        >
          {navItems.map((item) => {
            const isActive =
              location.pathname === item.path ||
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

            return (
              <Link
                key={item.label}
                to={item.path}
                onMouseEnter={(e) => {
                  if (!isCollapsed) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  setTooltip({
                    text: `${item.label}${item.badge !== undefined ? ` (${item.badge})` : ''}`,
                    top: rect.top + rect.height / 2,
                  });
                }}
                onMouseLeave={() => setTooltip(null)}
                className={`group relative flex items-center ${
                  isCollapsed ? 'justify-center p-2.5' : 'justify-between px-3 py-2'
                } rounded-lg text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-[#EAEFF5] text-slate-900 font-semibold'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-2.5'}`}>
                  <span className={isActive ? 'text-slate-900' : 'text-slate-400 group-hover:text-slate-700'}>
                    {item.icon}
                  </span>
                  {!isCollapsed && <span>{item.label}</span>}
                </div>

                {/* Badge */}
                {item.badge !== undefined && (
                  isCollapsed ? (
                    <span className="absolute top-1 right-1 px-1 py-0.2 rounded-full text-[9px] font-bold bg-[#DC2626] text-white">
                      {item.badge}
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-[#DC2626] text-white">
                      {item.badge}
                    </span>
                  )
                )}
              </Link>
            );
          })}
        </nav>

        {/* User profile pinned at bottom */}
        <div className="p-3 border-t border-slate-200 relative">
          <button
            type="button"
            onClick={() => setIsUserMenuOpen((prev) => !prev)}
            aria-label={displayName}
            onMouseEnter={(e) => {
              if (!isCollapsed) return;
              const rect = e.currentTarget.getBoundingClientRect();
              setTooltip({
                text: `${displayName} (Continuity lead)`,
                top: rect.top + rect.height / 2,
              });
            }}
            onMouseLeave={() => setTooltip(null)}
            className={`w-full flex items-center ${
              isCollapsed ? 'justify-center p-1.5' : 'space-x-3 p-2'
            } rounded-lg hover:bg-slate-100 transition-colors text-left`}
          >
            <div className="w-8 h-8 rounded-full bg-[#0F172A] text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
              {userInitials}
            </div>
            {!isCollapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-900 truncate">
                    {displayName}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">Continuity lead</div>
                </div>
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                </svg>
              </>
            )}
          </button>

          {isUserMenuOpen && (
            <div
              className={`absolute bottom-16 ${
                isCollapsed ? 'left-14 w-52' : 'left-3 right-3'
              } bg-white rounded-lg shadow-xl border border-slate-200 py-1 text-xs z-50`}
            >
              <Link
                to="/profile"
                className="block px-3.5 py-1.5 text-slate-700 hover:bg-slate-50"
                onClick={() => setIsUserMenuOpen(false)}
              >
                Profile & MFA
              </Link>
              <Link
                to="/subscription-management"
                className="block px-3.5 py-1.5 text-slate-700 hover:bg-slate-50"
                onClick={() => setIsUserMenuOpen(false)}
              >
                Subscriptions & Upgrades
              </Link>
              <Link
                to="/payment-history"
                className="block px-3.5 py-1.5 text-slate-700 hover:bg-slate-50"
                onClick={() => setIsUserMenuOpen(false)}
              >
                Payment & Invoices
              </Link>
              <Link
                to="/billing-settings"
                className="block px-3.5 py-1.5 text-slate-700 hover:bg-slate-50"
                onClick={() => setIsUserMenuOpen(false)}
              >
                Billing Settings
              </Link>
              <Link
                to="/crisis-communications"
                className="block px-3.5 py-1.5 text-slate-700 hover:bg-slate-50"
                onClick={() => setIsUserMenuOpen(false)}
              >
                Crisis Communications
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="block px-3.5 py-1.5 text-slate-700 hover:bg-slate-50 font-semibold border-t border-slate-100"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  Admin console
                </Link>
              )}
              <button
                type="button"
                onClick={() => {
                  setIsUserMenuOpen(false);
                  logout();
                }}
                className="w-full text-left px-3.5 py-1.5 text-rose-600 hover:bg-rose-50 border-t border-slate-100"
              >
                Sign out
              </button>
            </div>
          )}
        </div>

        {/* Floating Tooltip when collapsed */}
        {isCollapsed && tooltip && (
          <div
            style={{ top: tooltip.top }}
            className="fixed left-[88px] -translate-y-1/2 z-50 pointer-events-none flex items-center"
          >
            <div className="relative px-2.5 py-1 text-xs font-semibold text-white bg-slate-900 rounded-md shadow-xl whitespace-nowrap">
              <span className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-slate-900 rotate-45" />
              {tooltip.text}
            </div>
          </div>
        )}
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header Bar */}
        <header className="h-16 bg-white border-b border-slate-200 px-8 flex items-center justify-between flex-shrink-0 z-10">
          <div>
            {title && (
              <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">{title}</h1>
            )}
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>

          <div className="flex items-center space-x-3">
            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="relative w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-3.5 w-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search plans, functions, people"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg placeholder-slate-400 text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:bg-white transition-all"
              />
            </form>

            {/* Export Report */}
            <button
              type="button"
              onClick={onExportReport || (() => window.print())}
              className="px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold shadow-sm transition-colors"
            >
              Export report
            </button>

            {/* Action Button */}
            {primaryAction !== undefined ? (
              primaryAction
            ) : (
              <button
                type="button"
                onClick={onNewExercise || (() => navigate('/tabletop-exercises/new'))}
                className="px-3.5 py-1.5 bg-[#991B1B] hover:bg-[#7F1D1D] text-white rounded-lg text-xs font-bold shadow-sm transition-colors flex items-center space-x-1"
              >
                <span>+</span>
                <span>New exercise</span>
              </button>
            )}
          </div>
        </header>

        {/* Scrollable Page Body */}
        <main className="flex-1 overflow-y-auto bg-[#F8FAFC] px-6 sm:px-8 py-8">
          <div className="w-full">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default WorkspaceLayout;
